import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Inbox, Landmark, Bell, Loader2, Copy, Receipt, User } from "lucide-react";

const inr = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(v || 0));
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("en-IN") : "-";

export default function TenantApprovals() {
  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState({});
  const [invoices, setInvoices] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [approveFor, setApproveFor] = useState(null);
  const [acctId, setAcctId] = useState("");
  const [busy, setBusy] = useState(false);
  const [remBusy, setRemBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [pending, ts, invs, accs] = await Promise.all([
        api.get("/tenant-portal-admin/pending"),
        api.get("/tenants"), api.get("/invoices"), api.get("/bank-accounts"),
      ]);
      setRows(pending);
      setTenants(Object.fromEntries(ts.map((t) => [t.id, t])));
      setInvoices(Object.fromEntries(invs.map((i) => [i.id, i])));
      setAccounts(accs.filter((a) => a.status !== "inactive"));
      const primary = accs.find((a) => a.is_primary) || accs[0];
      if (primary) setAcctId(primary.id);
    } catch (e) { toast.error("Failed to load pending payments"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async () => {
    if (!approveFor) return;
    setBusy(true);
    try {
      const r = await api.post(`/tenant-portal-admin/pending/${approveFor.id}/approve`, { account_id: acctId || null });
      toast.success(`Approved · ${r.receipt_no}`); setApproveFor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approval failed"); }
    finally { setBusy(false); }
  };
  const reject = async (row) => {
    if (!window.confirm(`Reject ₹${row.amount.toLocaleString("en-IN")} from ${tenants[row.tenant_id]?.name || "tenant"}?`)) return;
    try { await api.post(`/tenant-portal-admin/pending/${row.id}/reject`, {}); toast.success("Rejected"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
  };
  const sendReminders = async () => {
    setRemBusy(true);
    try { const r = await api.post("/tenant-portal-admin/send-reminders", {}); toast.success(`${r.created} rent reminder${r.created === 1 ? "" : "s"} created`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send reminders"); }
    finally { setRemBusy(false); }
  };

  return (
    <div data-testid="tenant-approvals-page">
      <PageHeader title="Approval Inbox" subtitle={`${rows.length} pending payment${rows.length === 1 ? "" : "s"} from the tenant portal`} testid="approvals-header">
        <Button data-testid="send-reminders-btn" variant="outline" onClick={sendReminders} disabled={remBusy}>
          {remBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Bell className="w-4 h-4 mr-1" />} Send rent reminders now
        </Button>
      </PageHeader>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
      ) : rows.length === 0 ? (
        <Card className="border-2 border-dashed border-slate-200 shadow-none p-12 text-center" data-testid="approvals-empty">
          <Inbox className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <h3 className="font-heading text-lg font-semibold text-slate-700">Inbox zero</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">You're all caught up. When tenants submit an "I paid" reference from the portal, it'll appear here for one-click approval.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {rows.map((row) => {
            const t = tenants[row.tenant_id] || {};
            const linkedInvs = (row.invoice_ids || []).map((id) => invoices[id]).filter(Boolean);
            return (
              <Card key={row.id} className="border-slate-200 shadow-sm p-5 hover:shadow-md transition-shadow" data-testid={`pending-${row.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="font-heading text-2xl font-bold tracking-tighter text-slate-900" data-testid={`pending-amount-${row.id}`}>{inr(row.amount)}</p>
                    <p className="text-xs text-slate-500 mt-0.5">Submitted {fmtDate(row.created_at)}</p>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700 capitalize">{row.status}</Badge>
                </div>
                <dl className="space-y-1.5 text-sm border-t border-slate-100 pt-3">
                  <div className="flex items-center gap-2"><User className="w-4 h-4 text-slate-400" /><span className="text-slate-500 w-20">Tenant</span><span className="font-medium text-slate-800">{t.name || row.tenant_id}</span><span className="text-xs text-slate-400 font-mono">{t.code}</span></div>
                  <div className="flex items-center gap-2"><Landmark className="w-4 h-4 text-slate-400" /><span className="text-slate-500 w-20">Method</span><span className="font-medium text-slate-800">{row.method}</span></div>
                  <div className="flex items-start gap-2"><Copy className="w-4 h-4 text-slate-400 mt-0.5" /><span className="text-slate-500 w-20 shrink-0">Reference</span><span className="font-mono text-xs bg-slate-50 border border-slate-200 rounded px-2 py-0.5 break-all">{row.reference}</span></div>
                  <div className="flex items-start gap-2"><Receipt className="w-4 h-4 text-slate-400 mt-0.5" /><span className="text-slate-500 w-20 shrink-0">Invoices</span>
                    <span className="flex flex-wrap gap-1">{linkedInvs.map((i) => <span key={i.id} className="text-xs font-mono px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{i.invoice_no}</span>)}</span>
                  </div>
                  {row.note && <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 mt-2">{row.note}</div>}
                </dl>
                <div className="flex gap-2 mt-4">
                  <Button data-testid={`approve-${row.id}`} onClick={() => setApproveFor(row)} className="flex-1 bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4 mr-1" /> Approve</Button>
                  <Button data-testid={`reject-${row.id}`} variant="outline" onClick={() => reject(row)} className="text-rose-700 border-rose-200 hover:bg-rose-50"><XCircle className="w-4 h-4 mr-1" /> Reject</Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!approveFor} onOpenChange={(o) => !o && setApproveFor(null)}>
        <DialogContent className="max-w-md" data-testid="approve-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Approve payment</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            Confirming will create a real payment of <b>{inr(approveFor?.amount)}</b> from{" "}
            <b>{tenants[approveFor?.tenant_id]?.name || "the tenant"}</b> and mark the linked invoices paid.
          </p>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Deposit into (optional)</label>
            <select data-testid="approve-account" value={acctId} onChange={(e) => setAcctId(e.target.value)} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="">— Don't record to a bank account —</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} {a.is_primary ? "· Primary" : ""}</option>)}
            </select>
            <p className="text-xs text-slate-400 mt-1">Recommended: pick the account where the tenant's UPI actually landed so your ledger stays in sync.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveFor(null)} disabled={busy}>Cancel</Button>
            <Button data-testid="confirm-approve-btn" onClick={approve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Approve payment"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
